import { diagramConfig } from "./config/diagram.config";
import { DiagramEventListeners } from "./diagram-assets/diagram-event-listeners";
import { throwError, throwConsoleError } from "./diagram-assets/error-handling";
import { getParentSize, setCanvasSize } from "./diagram-assets/diagram-positioning-state";
import { DiagramCamera } from "./modules/DiagramCamera";
import { DiagramEventEmitter } from "./modules/DiagramEventEmitter";
import { API_CALLS_CONST } from "./config/apiCalls.config";
import { FabricDiagramStateHolder } from "../modules/FabricDiagramStateHolder";
import { DIAGRAM_EVENTS } from "./config/events.const";
import { DIAGRAM_EMIT_EVENTS } from "./modules/DiagramEvents";
import { keyboard } from "./models/keycodes.enum";

/**
 * @description Main class of the fabric diagram, initializez View Module and other util classes
 *              Wraps a closure around the class in order to save the instances references inside instance object.
 *              Uses a uuid as unique identifier for the instance
 * @returns {FabricDiagram} - Returns the class defined inside the IIFE
 */
// eslint-disable-next-line
export const FabricDiagram = (function () {
    const instance = {};
    // eslint-disable-next-line
    class FabricDiagram {
    canvas;

    ctx;

    plugins = [];

    data = null;

    config = {};

    eventHandlerInstance;

    activeKeys = new Map();

    viewInstance;

    dataInstance;

    diagramCamera;

    diagramEventEmitter;

    instanceUuid;

    // Holding dragging status. Only that for now.
    state = {};

    zoom = 1;

    /**
     * @constructor
     * @param {String} diagramInstanceUuid - A uuid defined for the diagram instance.
     *                                       Will be sent down the chain to the View Instance and other Util classes in order to instantiate
     */
    constructor(diagramInstanceUuid) {
        if (!instance[diagramInstanceUuid]) {
        // setting the singleton instance
            instance[diagramInstanceUuid] = this;
            this.instanceUuid = diagramInstanceUuid;
        }
        return instance[diagramInstanceUuid];
    }

    /**
     * @description - Initializez the fabric diagram instance by registering plugins,
     *                initializing view and data module, event listeners, validating initialization data
     * @param {HTMLCanvasElement} canvas - Canvas element reference;
     * @param {Object} data - The diagram model used for initializatiion
     * @param {Object} config - Config options. They will override the defaults
     * @returns void
     */
    init(canvas, data, config) {
        this.validateInitializationData(canvas, data, config);

        this.config = { ...diagramConfig() };
        if (config) this.config = { ...this.config, ...config };

        this.canvas = canvas;
        this.diagramEventEmitter = new DiagramEventEmitter(this.instanceUuid);
        this.diagramEventEmitter.registerCanvasElement(canvas);
        this.ctx = canvas.getContext("2d");
        this.registerPlugin(config.plugins);
        this.initStateManager();
        // Initialize Data
        this.initData(data);

        // Initialize View
        this.initView(this.ctx);
    }

    /**
     * @description - Creates a instance of FabricDiagramStateHolder used to redo and undo
     *              - Will be called from the redo and undo api's
     * @return void
     */
    initStateManager() {
        this.stateManager = new FabricDiagramStateHolder(this.instanceUuid);
        this.stateManager.init({ stateCacheMaxLength: this.config.stateCacheMaxLength });
    }

    /**
     * @description API Call used to zoom in the diagram
     * @param {Object} coords - Object of type {x,y}, representing the coordonates relative to the canvas context
     * @param {Object} unadjustedCoords - Object of type {x,y}, representing the coordonates unadjusted to the canvas context.
     * @returns void;
     */
    zoomIn(coords, unadjustedCoords) {
        const response = this.diagramCamera.setZoomLevel(0.1, coords, this, unadjustedCoords);
        if (response !== false) {
            this.zoom += 0.1;
            this.diagramCamera.draw(true);
        }
    }

    /**
     * @description API Call used to zoom out the diagram
     * @param {Object} coords - Object of type {x,y}, representing the coordonates relative to the canvas context
     * @param {Object} unadjustedCoords - Object of type {x,y}, representing the coordonates unadjusted to the canvas context.
     * @returns void;
     */
    zoomOut(coords, unadjustedCoords) {
        const response = this.diagramCamera.setZoomLevel(-0.1, coords, this, unadjustedCoords);
        if (response !== false) {
            this.zoom -= 0.1;
            this.diagramCamera.draw(true);
        }
    }

    /**
     * @description - Method used to reset the current zoom level to default value.
     * @param {Number} - zoomDiff
     * @returns void
     */
    resetZoom(zoomDiff) {
        if (zoomDiff) {
            this.zoom = 1;
            const response = this.diagramCamera.setZoomLevel(zoomDiff, null, this, null);
            if (response !== false) {
                this.diagramCamera.draw(true);
            }
        }
    }

    /**
     * @description - Method used to return the current zoom level.
     * @returns {Float} - Zoom level
     */
    getZoomLevel() {
        return this.zoom;
    }

    /**
     * @description API Method that retrieves the diagram JSON
     * @returns {Object} - diagram object model
     */
    getDiagramData() {
        return this.viewInstance.getDiagramData();
    }

    /**
     * @description - API Method used to set the diagram to readonly
     * @param {Boolean} val - Either true or false, value to be set to readonly.
     * @param {Boolean} oneAtATime - Either true or false, value to be set to only one user can edit diagram.
     * @returns void
     */
    setReadonly(val, oneAtATime) {
        const redraw = val !== this.config.readonly;
        this.config.readonly = val;
        this.config.oneAtATime = oneAtATime;
        if (redraw) {
            this.removeListeners();
            this.eventHandlerInstance.addListeners(this.canvas);
            this.changeDataSet(this.getDiagramData());
        }
    }

    /**
     * @description Returns the undo cache length
     * @returns {Number} - the cache length
     */
    getHistoryUndoCacheLength() {
        return this.stateManager.getStateCacheLength();
    }

    /**
     * @description Returns the redo cache length
     * @returns {Number} - the cache length
     */
    getHistoryRedoCacheLength() {
        return this.stateManager.getRedoCacheLength();
    }

    /**
     * @description - Performs undo on the diagram
     */
    undoState(emitEvents = true) {
        this.stateManager.performUndoState(emitEvents);
    }

    /**
     * @description - Performs redo on the diagram
     */
    redoState(emitEvents = true) {
        this.stateManager.performRedoState(emitEvents);
    }

    resetDiagramScroll() {
        this.viewInstance.resetDiagramScroll();
    }

    /**
     * @description - Sets the diagram to the provided diagram model
     * @param {Object} dataSet - Diagram object model used to set the diagram to
     */
    changeDataSet(dataSet, isDataModel) {
        this.dataInstance.data = dataSet;
        this.viewInstance.changeDataSet(dataSet, false, isDataModel);
    }

    /**
     * @description checks to see if the provided element is a zoom event, and calls the appropiate function (zoomIn or zoomOut)
     * @param {String} event - The name of the event type.
     * @param {HTMLElementEvent} payload - The Element Event emitted
     * @returns {Boolean}
     */
    isZoomEvent(event, payload) {
        if (event === DIAGRAM_EVENTS.KEYDOWN && payload.ctrlKey && payload.keyCode === keyboard.PLUS_SIGN) {
            payload.preventDefault();
            payload.stopPropagation();
            this.zoomIn();
            return true;
        }
        if (event === DIAGRAM_EVENTS.KEYDOWN && payload.ctrlKey && payload.keyCode === keyboard.MINUS_SIGN) {
            payload.preventDefault();
            payload.stopPropagation();
            this.zoomOut();
            return true;
        }
        if (event === DIAGRAM_EVENTS.MOUSE_WHEEL && payload.payload.ctrlKey) {
            payload.payload.preventDefault();
            payload.payload.stopPropagation();
            let wheelCondition = false;
            if (payload.payload.wheelDelta) {
                wheelCondition = payload.payload.wheelDelta < 0;
            } else {
                wheelCondition = payload.payload.deltaY > 0;
            }
            if (wheelCondition) {
                this.zoomOut(payload.coords, { x: payload.payload.layerX, y: payload.payload.layerY });
            } else {
                this.zoomIn(payload.coords, { x: payload.payload.layerX, y: payload.payload.layerY });
            }
            return true;
        }
        return false;
    }

    /**
     * @description API call used to remove all cell independent pluginss
     * @returns { void }
     */
    removeAllCellIndependentPlugins() {
        this.viewInstance.apiCallHandler(API_CALLS_CONST.REMOVE_ALL_CELL_INDEPENDENT_PLUGINS);
    }

    /**
     * @description Sends the event further to the view instance
     * @param {String} event - The name of the event type.
     * @param {HTMLElementEvent} payload - The Element Event emitted
     * @returns { void }
     */
    sendPluginRelatedEvent(event, payload) {
        if (this.isZoomEvent(event, payload)) {
            return;
        }
        if (event === DIAGRAM_EVENTS.MOUSE_WHEEL) {
            payload = payload.payload;
            const element = document.getElementById("customLabelId");
            if (element) {
                element.blur();
            }
        }

        this.handleCanvasCursor(event, payload);
        // Hardcoded to false due to this functionality being used through the API only
        // this.handleHistoryAction(event, payload);
        const isHistoryAction = false;
        if (!isHistoryAction) {
            this.viewInstance.eventHappening(event, payload);
        }
    }

    /**
     * @description Calls undo / redo based on the pressed keys
     * @param {String} event - The event name
     * @param {Object} payload - {x,y} object
     * @returns { Boolean } - true if is a redo / undo event, false otherwise. Will not send the event further to the view if is a undo / redo event
     */
    handleHistoryAction(event, payload) {
        if (event === DIAGRAM_EVENTS.KEYDOWN && ((payload.event && payload.event.ctrlKey) || payload.ctrlKey)) {
            if (payload.keyCode === keyboard.KEY_Z) {
                this.undoState();
            } else if (payload.keyCode === keyboard.KEY_Y) {
                this.redoState();
            }
            return true;
        }
        return false;
    }

    /**
     * @description - Changes the cursor of the canvas depending on the event
     * @param { String } event - A string containing the name of the event
     * @returns { void }
     */
    handleCanvasCursor(event, payload) {
        const container = document.querySelector(`#${this.config.containerID}`);
        if (!container) {
            return;
        }
        if (this.viewInstance.isClickableElement({ x: payload.x, y: payload.y })) {
            this.canvas.style.cursor = "pointer";
        } else if (event === DIAGRAM_EVENTS.MOUSE_DOWN) {
            this.canvas.style.cursor = "grabbing";
        } else if (event === DIAGRAM_EVENTS.MOUSE_UP) {
            this.canvas.style.cursor = "grab";
        } else if (payload.event && payload.event.buttons === 1 && event === DIAGRAM_EVENTS.MOUSE_MOVE) {
            // make sure left click is not being pressed during mouse move
            if (this.canvas.style.cursor !== "grabbing") {
                this.canvas.style.cursor = "grabbing";
            }
        } else if (this.canvas.style.cursor !== "grab") {
            this.canvas.style.cursor = "grab";
        }
    }

    /**
  * @description Create a cell independent container on the grid
  * @param {string} pluginID -A string representing the ID of the plugin. Ex: DATA_MODEL
  * @param {object} layout - A object containing the properties: {width,height,x,y}.
  *                         If width,height is not provided the default is used, if x,y is not provided a place on the board is searched.
  *                         If no place is found, a error event is emited.
  * @param {object} state - A object containing the properties: {selected}. If selected is provided and is true, the plugin will be selected upon creation
  * @param {object} data - A object containing the properties: {label(string), children(objectArray)}. If label is not provided, the default will be used
  * @return { void }
  */
    addCellIndependentContainer(pluginID, layout, state, data) {
        this.viewInstance.apiCallHandler(API_CALLS_CONST.ADD_CELL_INDEPENDENT_CONTAINER, {
            pluginID, layout, state, data
        });
    }

    /**
     * @description - Update plugin with the uuid "pluginUuid" anchored to the element with uuid "anchorPluginUuid"
     * @param {String} options.pluginUuid - The anchored plugin UUID to be updated
     * @param {String} options.anchorPluginUuid  - The uuid of the plugin that the anchored plugin is anchored to
     * @param {Object} options.options - An object with the properties: {position, offset, data}
     * @returns { void }
     */
    updatePluginAnchoredElement(options) {
        this.viewInstance.apiCallHandler(API_CALLS_CONST.UPDATE_ANCHORED_PLUGIN, options);
    }

    /**
     * @param {Object} state - the state to be used for updating the plugin
     * @param {String} pluginUuid - The anchored plugin UUID to be updated
     * @param {Boolean} isCellIndependent - indicates if is a cell independent plugin or the child of a cell indepenent plugin
     * @param {Boolean} isConnector - indicates if the pluginUuid is for a connector
     * @description Updates the state of the given pluginUuid with the provided state
     * @returns { void }
     */
    updatePluginState(state, pluginUuid, isCellIndependent, isConnector) {
        if (!state) {
            // console.error("Please provide a state when update a plugin state! Canceling call...");
            return;
        }
        if (state && state.hasOwnProperty("selected")) {
            // console.error("Please use the plugin selection API for selection status! Canceling call...");
            return;
        }
        this.viewInstance.apiCallHandler(API_CALLS_CONST.UPDATE_PLUGIN_STATE, {
            state, pluginUuid, isCellIndependent, isConnector
        });
    }

    /**
     * @description - Update plugin design
     * @param {Object} options  - An object with properties to update plugin design
     * @returns { void }
     */
    updatePluginDesign(options) {
        this.viewInstance.apiCallHandler(API_CALLS_CONST.UPDATE_PLUGIN_DESIGN, options);
    }

    /**
       * @param { String } anchoredTo - Uuid of the plugin that the element is anchored to
       * @param { String } pluginID - The ID of the plugin that must be created as anchored element. Possible value: "ANCHORED_HEXAGON"
       * @param { String } anchorType - The type of the plugin that the element is anchored to. Possbile value: "CELL_BASED"
       * @param { String } position - Default Value: TOP_LEFT // Possible Values:
       *                            "TOP_LEFT", "BOTTOM_LEFT", "CENTER_LEFT",
       *                            "TOP_CENTER", "CENTER_CENTER", "BOTTOM_CENTER",
       *                            "TOP_RIGHT", "CENTER_RIGHT", "BOTTOM_RIGHT",
       * @param { Object } offset - Object of type: {x:numericValue, y: numericValue}.
       *                            Positive values will move the element to the right, or bottom of the initial position
       * @param { Object } data - Object of type: {text:'5', fillStyle: desiredColor} that will be set inside the anchored element.
       * @returns { String } - the uuid of the newly added plugin
       */
    addPluginAnchoredElement(anchoredTo, pluginID, anchorType, position, offset, data) {
        return this.viewInstance.apiCallHandler(API_CALLS_CONST.ADD_ANCHORED_PLUGIN, {
            anchoredTo, pluginID, anchorType, position, offset, data
        });
    }

    /**
      * @param {string} containerUuid - The uuid of the container
      * @param {object} data - A object containing the properties: {label(string)}.
      * @description Updates the data of a cell independent container
      * @returns { void }
      */
    updateCellIndependentContainer(containerUuid, data) {
        this.viewInstance.apiCallHandler(API_CALLS_CONST.UPDATE_CELL_INDEPENDENT_CONTAINER, { containerUuid, data });
    }

    /**
    * @param {string} containerUuid - The uuid of the container
    * @param {string} childPluginID - The plugin ID to be used for child creation. Ex - DATA_MODEL_PROPERTY
    * @param {object} state - A object containing the properties: {selected}. If selected is provided and is true, the plugin will be selected upon creation
    * @param {object} data - A object containing the properties: {label(string)}. If label is not provided, the default will be used
    * @description Creates a child inside a container based on the provided params
    * @returns {String} - the uuid of the newly created pluign
    */
    addCellIndependentContainerChild(containerUuid, childPluginID, state, data) {
        return this.viewInstance.apiCallHandler(API_CALLS_CONST.ADD_CELL_INDEPENDENT_CONTAINER_CHILD, {
            containerUuid, childPluginID, state, data
        });
    }

    /**
     * @param {string} containerUuid - The uuid of the container
     * @param {string} childPluginUUID - The uuid of the child
     * @param {object} data - A object containing the properties: {label(string)}.
     * @description Updates the label of a child inside a cell independent container
     * @returns { void }
     */
    updateCellIndependentContainerChild(containerUuid, childPluginUUID, data) {
        this.viewInstance.apiCallHandler(API_CALLS_CONST.UPDATE_CELL_INDEPENDENT_CONTAINER_CHILD, { containerUuid, childPluginUUID, data });
    }

    /**
   * @param {string} containerUuid - The uuid of the container
   * @param {string} childPluginUUID - The uuid of the child
   * @description Removes a child plugin from a cell independent container
   * @returns { void }
   */
    removeCellIndependentContainerChild(containerUuid, childPluginUUID) {
        this.viewInstance.apiCallHandler(API_CALLS_CONST.REMOVE_CELL_INDEPENDENT_CONTAINER_CHILD, { containerUuid, childPluginUUID });
    }

    /**
    * @param {string} containerUuid - The uuid of the container
    * @description Removes a cell independent container
    * @returns { void }
    */
    removeCellIndependentContainer(containerUuid) {
        this.viewInstance.apiCallHandler(API_CALLS_CONST.REMOVE_CELL_INDEPENDENT_CONTAINER, { containerUuid });
    }

    /**
    * @description Provide the UUID of the from and to plugins and it will create a connection between the two
    * @param {String} from
    * @param {String} to
    * @returns {String} returns the new connector UUID
    */
    addConnector(from, to) {
        return this.viewInstance.apiCallHandler(API_CALLS_CONST.ADD_CONNECTOR, { from, to });
    }

    /**
     * @param {String} uuid - the uuid of the connector to delete
     * @description Deletes a connector with the provided UUID
     * @returns { void }
     */
    deleteConnector(uuid) {
        this.viewInstance.apiCallHandler(API_CALLS_CONST.DELETE_CONNECTOR, { uuid });
    }

    /**
     * @description - Updates the label of a connector with the provided uuid
     * @param { String } connectorUUID - The connector uuid who's label needs to be updated
     * @param { String } label - The new label to be set
     * @returns { void }
     */
    updateConnectorLabel(connectorUUID, label) {
        this.viewInstance.apiCallHandler(API_CALLS_CONST.UPDATE_CONNECTOR_LABEL, { connectorUUID, label });
    }

    /**
     * @description Deletes the plugin with the provided uuid
     * @param { String } uuid - The uuid of the plugin to delete
     * @returns { void }
     */
    deletePlugin(uuid) {
        this.viewInstance.apiCallHandler(API_CALLS_CONST.DELETE_PLUGIN, { uuid });
    }

    /**
     * @description - Updates a plugins label
     * @param { String }  pluginUuid - The plugin uuid who's label will be updated
     * @param { String } label - the new label value
     * @returns { void }
     */
    updatePluginLabel(pluginUuid, label, noEvent) {
        this.viewInstance.apiCallHandler(API_CALLS_CONST.UPDATE_PLUGIN_LABEL, { pluginUuid, label, noEvent });
    }

    /**
     * @description - Marks a connector as selected and deselets any other element that might be currently selected
     * @param { String } connectorUuid  - The uuid of the connector
     * @returns { void }
     */
    selectConnector(connectorUuid, emitEvent) {
        this.viewInstance.apiCallHandler(API_CALLS_CONST.SELECT_CONNECTOR, { connectorUuid, emitEvent });
    }

    /**
     * @description Marks a plugin as selected and deselects any other element that might be currrently selected
     * @param { String } pluginUuid - The uuid of the element
     * @param { Boolean } isCellIndependent - A boolean that indicates if the specifiecd plugin uuid is for a cell indepenendent plugin
     */
    selectPlugin(pluginUuid, isCellIndependent, triggerEvent) {
        this.viewInstance.apiCallHandler(API_CALLS_CONST.SELECT_PLUGIN, { pluginUuid, isCellIndependent, triggerEvent });
    }

    /**
     * @description - Redraws the view and camera scrollbars
     * @param { Object } viewport - The current visible area inside, the viewport
     * @param { Boolean } fromResize - Indicates if the view redraw request comes from the resize event. If yes, it will also redraw the camera
     * @return { void }
     */
    requestViewRedraw(viewport, fromResize) {
        if (fromResize) {
            this.diagramCamera.draw(true);
            return;
        }
        if (this.viewInstance) {
            this.viewInstance.draw(viewport || this.diagramCamera.lastViewport);
        }
    }

    /**
     * @description Initializez a data handler. The view pulls the initial data from this object.
     * @param { Object } data - diagram object model
     * @return { void }
     */
    initData(data) {
        // eslint-disable-next-line
        this.dataInstance = new this.config.dataHandler(this.instanceUuid);
        this.dataInstance.init(data);
    }

    /**
     * @description Initializez the view handler (gridView), diagram event listeners and the diagram camera
     * @param { CanvasRenderingContext2D } ctx
     * @return { void }
     */
    initView(ctx) {
        // eslint-disable-next-line
        this.viewInstance = new this.config.view(this.instanceUuid);
        this.diagramCamera = new DiagramCamera(ctx.canvas, this);
        const viewInstanceInitData = this.viewInstance.init(ctx, this.dataInstance, this.diagramCamera, this);
        // eslint-disable-next-line
        const initialCamera = {
            x: viewInstanceInitData.x,
            y: viewInstanceInitData.y
        };

        this.stateManager.setViewInstance(this.viewInstance);
        this.setInitialCanvasSize();
        this.diagramCamera.init();
        setTimeout(() => {
            this.diagramCamera.draw();
            // Create a instance of DiagramEventListener, save its reference.
            // The DiagramEventListeners constructor will automaticaly call the register events function.
            this.eventHandlerInstance = new DiagramEventListeners(this);
        // this.diagramCamera.scrollbars.scrollBy(
        //   initialCamera.x,
        //   initialCamera.y
        // );
        }, 50);
        this.diagramEventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.DIAGRAM_VIEW_INITIALIZED);
    }

    /**
     * @description - Sets the initial canvas size based on the canvas parent div
     * @returns { void }
     */
    setInitialCanvasSize() {
        const parentSize = getParentSize(this.config.containerID);
        setCanvasSize(this.canvas, parentSize.width, parentSize.height);
    }

    /**
     * @description Checks to see if the diagram was initialzied with all the mandatory properties
     * @param { Object } canvas - Canvas reference
     * @param { Object } data - Diagram object model
     * @param { Object } config - Diagram config object, { readonly, containerID }
     * @returns { void }
     */
    validateInitializationData(canvas, data, config) {
        const noPluginsMsg = "Please provide at least one plugin before calling initialization!";
        const missesPlugins = (!config || (config && !config.plugins) || (config && !config.plugins.length)) && !this.plugins.length;
        const missesView = (!config || (config && !config.view)) && !this.view;
        const missesDataHandler = (!config || (config && !config.dataHandler)) && !this.dataHandler;
        if (!canvas) throwError("Please provide a canvas reference!");
        if (!data) throwError("Please provide a data object for initialization!");
        if (!config || (config && !config.containerID)) throwConsoleError("Please provide a container ID to be used for resizing the canvas!");
        if (missesPlugins) throwError(noPluginsMsg);
        if (missesView) throwError("Please provide a View Handler before initializing diagram!");
        if (missesDataHandler) throwError("Please provide a Data Handler before initializing diagram!");
    }

    /**
     * @param {Object[]} plugins
     * @description Registers a plugin inside the fabric diagram instance
     */
    registerPlugin(plugins) {
        if (!plugins) throwError("You need to provide one or more plugins when calling registerPlugin method");
        this.plugins = [
            ...this.plugins,
            ...(Array.isArray(plugins) ? plugins : [plugins])
        ];
    }

    /**
     * @description - Removes the diagram event listeners from the eventHandler instance
     * @returns { void }
     */
    removeListeners() {
        this.eventHandlerInstance.removeListeners(this.canvas);
    }

    /**
     * @description - Destroys all the references of the current diagram
     * @returns { void }
     */
    destroyDiagram() {
        this.eventHandlerInstance.removeListeners(this.canvas);
        if (this.viewInstance) this.viewInstance.clearView(this.instanceUuid);

        this.viewInstance = null;
        this.plugins = [];
        this.view = null;
        this.config = {};
        if (this.dataHandler) this.dataHandler.destroy(this.instanceUuid);
        this.stateManager.destroy(this.instanceUuid);
        this.diagramEventEmitter.destroy(this.instanceUuid);
        instance[this.instanceUuid] = null;
        delete instance[this.instanceUuid];
    }
    }

    return FabricDiagram;
}());
